import { z } from "zod";

// Field type enum
export const PageSchemaFieldTypeSchema = z.enum([
    'text',
    'textarea',
    'richtext',
    'number',
    'boolean',
    'select',
    'image',
    'partner_badge',
    'service_category',
    'service_subcategory',
    'country',
    'experts-smart-filter',
    'link_groups'
]);

// Search role enum for field-level search configuration
export const SearchRoleSchema = z.enum(['label', 'image']);

// Field definition schema
export const PageSchemaFieldSchema = z.object({
    key: z.string(),
    label: z.string(),
    type: PageSchemaFieldTypeSchema,
    required: z.boolean().optional().default(false),
    default_value: z.unknown().nullable().optional(),
    options: z.array(z.string()).optional().default([]),
    // Search configuration
    search_role: z.array(SearchRoleSchema).optional(),
    search_role_priority: z.number().min(1).max(1000).optional(),
});

// Searchability enum for schema-level search configuration
export const SearchabilitySchema = z.enum(['searchable', 'non-searchable']);

// Full page schema response (for admin)
export const PageSchemaSchema = z.object({
    id: z.number(),
    name: z.string(),
    label: z.string(),
    description: z.string().nullable().optional(),
    fields: z.array(PageSchemaFieldSchema),
    // Search configuration
    searchability: SearchabilitySchema.optional().default('non-searchable'),
    group_label: z.string().nullable().optional(),
    group_view_all_label: z.string().nullable().optional(),
    group_view_all_href: z.string().nullable().optional(),
    search_group_sort_index: z.number().nullable().optional(),
    // Metadata
    pages_count: z.number().optional(),
    created_at: z.string(),
    updated_at: z.string(),
});

// Create page schema request body
export const CreatePageSchemaRequestSchema = z.object({
    name: z.string(),
    label: z.string(),
    description: z.string().nullable().optional(),
    fields: z.array(PageSchemaFieldSchema),
    // Search configuration
    searchability: SearchabilitySchema.optional(),
    group_label: z.string().nullable().optional(),
    group_view_all_label: z.string().nullable().optional(),
    group_view_all_href: z.string().nullable().optional(),
    search_group_sort_index: z.number().nullable().optional(),
});

// Update page schema request body
export const UpdatePageSchemaRequestSchema = z.object({
    name: z.string().optional(),
    label: z.string().optional(),
    description: z.string().nullable().optional(),
    fields: z.array(PageSchemaFieldSchema).optional(),
    // Search configuration
    searchability: SearchabilitySchema.optional(),
    group_label: z.string().nullable().optional(),
    group_view_all_label: z.string().nullable().optional(),
    group_view_all_href: z.string().nullable().optional(),
    search_group_sort_index: z.number().nullable().optional(),
});

// TypeScript type exports
export type PageSchemaFieldType = z.infer<typeof PageSchemaFieldTypeSchema>;
export type SearchRole = z.infer<typeof SearchRoleSchema>;
export type PageSchemaField = z.infer<typeof PageSchemaFieldSchema>;
export type Searchability = z.infer<typeof SearchabilitySchema>;
export type PageSchema = z.infer<typeof PageSchemaSchema>;
export type CreatePageSchemaRequest = z.infer<typeof CreatePageSchemaRequestSchema>;
export type UpdatePageSchemaRequest = z.infer<typeof UpdatePageSchemaRequestSchema>;

